/**
 * `review` procedure group — the unified confirmation queue (design §2.8).
 *
 * "One place for everything the system guessed and a human confirms." Three
 * sources feed it in this wave, and they are deliberately merged SERVER-SIDE
 * into one cursor-paginated feed rather than left as three client fetches:
 *
 *  1. **suggested contacts** — a person who showed up as a calendar attendee, an
 *     email sender or a LinkedIn correspondent and matched no contact;
 *  2. **pending field candidates** — `contact_field_candidates` the merge layer
 *     parked instead of auto-applying (design §3.2);
 *  3. **tier-B phone links** — a device match good enough to propose, not good
 *     enough to link (design-clients §3.2, match tiers);
 *  4. **duplicate-contact signals** — an email address that matched two or more
 *     contacts during mail sync (design §3.3, added Wave-2 track D2).
 *
 * ## The one non-obvious shape: `contentHash` and dismissal
 *
 * Dismissal writes a CONTENT-HASH TOMBSTONE, not a row-id one. The reason is in
 * the design: an iCloud resync, a re-delivered calendar invite or a second
 * LinkedIn capture of the same person all mint a NEW row for the SAME human. A
 * row-id tombstone would re-ask the identical question every time. The hash is
 * over the normalized identity keys, so "no, this is not a contact" sticks to
 * the person rather than to the row that happened to raise it.
 *
 * That is also why `contentHash` is on the wire: the client shows it in no UI,
 * but a queue that dismissed by id would be indistinguishable from one that
 * dismissed by hash until the second sync — a bug with a week-long feedback
 * loop.
 *
 * ## Why `id` is a string, not a uuid
 *
 * The four sources are four tables. `id` is `"<kind>:<uuid>"` so one queue can
 * address all of them without a per-kind endpoint, and so a client cannot
 * accidentally send a candidate id to the phone-link path. It is opaque:
 * `review.resolve` parses it, nothing else does.
 */

import { z } from 'zod';
import {
  cursorSchema,
  isoDateTimeSchema,
  jsonObjectSchema,
  paginationInputSchema,
  uuidSchema,
} from './common.ts';

/** Which source raised the item. Also selects how `resolve` applies an accept. */
export const REVIEW_ITEM_KINDS = [
  'suggested_contact',
  'field_candidate',
  'phone_link',
  /**
   * Design §3.3: "an address matching 2+ contacts is a CRM DUPLICATE signal ->
   * merge suggestion, NEVER a silent pick." The fourth source, added with mail
   * sync. Accepting it means ACKNOWLEDGED — the merge itself is a separate,
   * destructive operation with its own undo story.
   */
  'duplicate_contact',
] as const;
export type ReviewItemKind = (typeof REVIEW_ITEM_KINDS)[number];
export const reviewItemKindSchema = z.enum(REVIEW_ITEM_KINDS);

/**
 * Where a suggested contact came from. Mirrors `suggested_contacts.source`; the
 * CHECK constraint in the migration is the authority.
 */
export const SUGGESTION_SOURCES = [
  'calendar_attendee',
  'email_sender',
  'linkedin_message',
  'linkedin_connection',
  'linkedin_profile',
] as const;
export type SuggestionSource = (typeof SUGGESTION_SOURCES)[number];
export const suggestionSourceSchema = z.enum(SUGGESTION_SOURCES);

export const REVIEW_ACTIONS = ['accept', 'dismiss'] as const;
export type ReviewAction = (typeof REVIEW_ACTIONS)[number];
export const reviewActionSchema = z.enum(REVIEW_ACTIONS);

/**
 * One queue row.
 *
 * Flat rather than a discriminated union of three shapes: the queue renders one
 * card layout with a kind badge, the keyboard model is identical across kinds,
 * and a union would force every client to switch before it could show a title.
 * Kind-specific members are nullable and documented with which kind fills them.
 */
export const reviewItemSchema = z.object({
  /** `"<kind>:<uuid>"`. Opaque; only `review.resolve` parses it. */
  id: z.string().min(3).max(80),
  kind: reviewItemKindSchema,
  /** The dismissal key. See the module header. */
  contentHash: z.string().min(1).max(128),
  /** One line, already formatted for the card headline. */
  title: z.string(),
  /** One line of supporting evidence, already formatted. */
  detail: z.string(),

  /** `field_candidate` and a linked `phone_link`: the contact in question. */
  contactId: uuidSchema.nullable(),
  contactName: z.string().nullable(),

  /** `suggested_contact`: how we met them. Null for the other kinds. */
  suggestionSource: suggestionSourceSchema.nullable(),
  /** `suggested_contact`: the identity we would create the contact from. */
  email: z.string().nullable(),
  linkedinSlug: z.string().nullable(),
  companyName: z.string().nullable(),

  /** `field_candidate`: which field, and what value is proposed. */
  fieldPath: z.string().nullable(),
  proposedValue: z.unknown(),
  provider: z.string().nullable(),

  /** `phone_link`: the match tier that pushed it up for confirmation. */
  matchTier: z.enum(['A', 'B', 'C']).nullable(),

  /**
   * `duplicate_contact`: EVERY contact the ambiguous key matched (always >= 2).
   * Plural on purpose — a singular field would force the server to pick one,
   * which is the behaviour this kind exists to refuse.
   */
  duplicateContactIds: z.array(uuidSchema),

  confidence: z.number().min(0).max(1).nullable(),
  /** How many times this same person/value has been seen. Never below 1. */
  occurrences: z.int().min(1),
  /** Typed evidence refs, exactly as stored. Rendered verbatim, never parsed. */
  evidence: jsonObjectSchema,
  firstSeenAt: isoDateTimeSchema,
  lastSeenAt: isoDateTimeSchema,
});

export const reviewCountsSchema = z.object({
  total: z.int().min(0),
  suggestedContacts: z.int().min(0),
  fieldCandidates: z.int().min(0),
  phoneLinks: z.int().min(0),
  duplicateContacts: z.int().min(0),
});

// --- inputs ------------------------------------------------------------------

export const reviewListInputSchema = paginationInputSchema.extend({
  /** Absent = every kind, interleaved newest-first. */
  kind: z.array(reviewItemKindSchema).max(4).optional(),
});

export const reviewListOutputSchema = z.object({
  items: z.array(reviewItemSchema),
  nextCursor: cursorSchema.nullable(),
  /** The same numbers `review.counts` returns, so the shell needs one call. */
  counts: reviewCountsSchema,
});

export const reviewResolveInputSchema = z.object({
  /** The opaque `"<kind>:<uuid>"` from `review.list`. */
  itemId: z.string().min(3).max(80),
  action: reviewActionSchema,
  /** Stored on a dismissal so the queue can explain why it stopped asking. */
  reason: z.string().max(500).optional(),
});

export const reviewResolveOutputSchema = z.object({
  ok: z.literal(true),
  itemId: z.string(),
  kind: reviewItemKindSchema,
  action: reviewActionSchema,
  /**
   * The tombstone written on a dismissal. Null on an accept — accepting is not
   * a "never ask again", it is an answer.
   */
  tombstonedContentHash: z.string().nullable(),
  /** Set when accepting a `suggested_contact` created a contact. */
  contactId: uuidSchema.nullable(),
  counts: reviewCountsSchema,
});

export const reviewCountsInputSchema = z.object({});

export type ReviewItem = z.output<typeof reviewItemSchema>;
export type ReviewCounts = z.output<typeof reviewCountsSchema>;
export type ReviewListInput = z.input<typeof reviewListInputSchema>;
export type ReviewResolveInput = z.input<typeof reviewResolveInputSchema>;
